import React, { forwardRef, ReactNode, useImperativeHandle, useRef } from 'react';
import { getLocale } from 'umi';
import classNames from 'classnames';
import { Bubble, Button, Icon, Modal, Transfer } from 'tea-component';
import { getName } from '@home/utils/common';
import { LANGUAGE_EN } from '@home/_config/config';
import { dynamicString } from '@home/utils/dynamicString';
import SvgIcon from '../SvgIcon';
import SearchSelector from './components/SearchSelector';
import TabsHeader from './components/TabsHeader';
import OptionBox from './components/OptionBox';
import DynamicOptionBox from './components/DynamicOptionBox';
import useTransferModalData from './hooks/useTransferModalData';
import { ITransferModalProps, ITransferModalRef } from './type';
import './index.scss';

const TransferModal = (
  props: ITransferModalProps,
  ref?: React.Ref<ITransferModalRef>,
) => {
  const {
    style,
    className,
    modalSize,
    titleCn,
    titleEn,
    subTitleCn,
    subTitleEn,
    infoCn,
    infoEn,
    leftTitleCn = '添加',
    leftTitleEn = 'Add',
    rightTitleCn = '已选({{length}})',
    rightTitleEn = 'Selected({{length}})',
    rightInfoEn,
    rightInfoCn,
    rightSubTitleCn,
    rightSubTitleEn,
    leftEmptyTextCn = '暂无数据',
    leftEmptyTextEn = 'No Data',
    rightEmptyTextCn = '请选择',
    rightEmptyTextEn = 'Please select',
    searchPlaceholderCn = '搜索',
    searchPlaceholderEn = 'Search',
    clearAllTextCn = '清空',
    clearAllTextEn = 'Clear All',
    confirmTextEn = 'Confirm',
    confirmTextCn = '确认',
    cancelTextEn = 'Cancel',
    cancelTextCn = '取消',
    maxLengthTextEn = 'Maximum limit is {{length}} games.',
    maxLengthTextCn = '最大不超过{{length}}个游戏.',
    isEN,
    maxLength,
    minLength = 1,
    visible = false,
    searchable = true,
    showClearAll = true,
    virtual = false,
    dynamicHeight = false,
    rowHeight,
    leftListHeight,
    rightListHeight,
    maxVisibleRows,
    leftOptions,
    rightOptions,
    leftLoading,
    rightLoading,
    loadingTextEn,
    loadingTextCn,
    searchOptions,
    quickOptions,
    quickValue,
    primaryKey,
    quickKey,
    onChangeQuickOptions,
    leftOptionRender,
    rightOptionRender,
    rightCustomRender,
    onCancel,
    onConfirm,
    onClickLeftOption,
    // onRemoveRightOption,
    onClearAllOption: propsOnClearAllOption,
    onSearch,
    onSearchValueChange,
    onClearSearch: propsOnClearSearch,
    filter,
    // 待补充
    filterRender,
    // 左侧tabs
    leftTabs,
    leftActiveTabId,
    onLeftTabChange,
    leftHeaderExtra,
    resetScrollOnOptionsChange = false, // 新增 prop，默认不重置滚动
    saveEmptyDisable = true
  } = props;

  const leftListRef = useRef<any>(null);
  const rightListRef = useRef<any>(null);

  const isEn = isEN ?? getLocale() === LANGUAGE_EN;

  const title = getName(isEn, titleEn, titleCn);
  const subTitle = getName(isEn, subTitleEn, subTitleCn);
  const infoText = getName(isEn, infoEn, infoCn);
  const leftTitle = getName(isEn, leftTitleEn, leftTitleCn);
  const rightTitle = getName(isEn, rightTitleEn, rightTitleCn);
  const rightInfo = getName(isEn, rightInfoEn, rightInfoCn);
  const rightSubTitle = getName(isEn, rightSubTitleEn, rightSubTitleCn);
  const leftEmptyText = getName(isEn, leftEmptyTextEn, leftEmptyTextCn);
  // const searchEmptyText = getName(isEn, searchEmptyTextEn, searchEmptyTextCn);
  const rightEmptyText = getName(isEn, rightEmptyTextEn, rightEmptyTextCn);
  const searchPlaceholder = getName(
    isEn,
    searchPlaceholderEn,
    searchPlaceholderCn,
  );
  const clearAllText = getName(isEn, clearAllTextEn, clearAllTextCn);
  const confirmText = getName(isEn, confirmTextEn, confirmTextCn);
  const cancelText = getName(isEn, cancelTextEn, cancelTextCn);
  const maxLengthText = getName(isEn, maxLengthTextEn, maxLengthTextCn);
  const loadingText = getName(isEn, loadingTextEn, loadingTextCn);

  const {
    currentLeftOptions,
    currentRightOptions,
    currentSearchOptions,
    currentQuickValue,
    onCheckLeftOption,
    onClearAllOption,
    onClearSearch,
    onClickQuickOption,
    onSearchChange,
    onSearchEnter,
  } = useTransferModalData({
    leftOptions,
    rightOptions,
    primaryKey,
    quickKey,
    quickValue,
    searchOptions,
    maxLength,
    onSearch,
    onSearchValueChange,
    onChangeQuickOptions,
    onClickLeftOption,
    onClearAllOption: propsOnClearAllOption,
    onClearSearch: propsOnClearSearch,
    filter,
    filterRender,
  });

  // 对外暴露函数
  useImperativeHandle(ref, () => ({
    // 获取当前右侧数据
    getRightOptions: () => currentRightOptions,
    // 滚动左侧列表到顶部
    scrollLeftToTop: () => {
      leftListRef.current?.scrollToTop?.();
    },
    // 滚动右侧列表到顶部
    scrollRightToTop: () => {
      rightListRef.current?.scrollToTop?.();
    },
    // 重新计算左侧列表行高
    recomputeLeftRowHeights: () => {
      leftListRef.current?.recomputeRowHeights?.();
    },
    // 重新计算右侧列表行高
    recomputeRightRowHeights: () => {
      rightListRef.current?.recomputeRowHeights?.();
    },
  }));

  // 标题
  const titleNode = (
    <>
      <div className="dbh-modal-title">
        <div>{title}</div>
        {infoText && (
          <Bubble arrowPointAtCenter content={infoText}>
            <SvgIcon type="avc" />
          </Bubble>
        )}
      </div>
      {subTitle && <div className="dbh-modal-sub-title">{subTitle}</div>}
    </>
  );

  const getCenterText = (text: ReactNode) => (
    <div className="dbh-transfer-modal-no-data">{text}</div>
  );

  const loadingNode = (
    <div className='dbh-loading-text'>
      <Icon size="l" type="loading" style={{ marginRight: 6 }} />
      {loadingText}
    </div>
  );

  // 左侧内容
  const getLeftNode = () => {
    if (leftLoading) {
      return getCenterText(loadingNode);
    }
    if (currentLeftOptions.length > 0) {
      const OptionComponent = dynamicHeight ? DynamicOptionBox : OptionBox;
      return (
        <OptionComponent
          ref={leftListRef}
          options={currentLeftOptions}
          onChange={onCheckLeftOption}
          optionRender={leftOptionRender}
          virtual={virtual}
          dynamicHeight={dynamicHeight}
          rowHeight={rowHeight}
          listHeight={leftListHeight}
          maxVisibleRows={maxVisibleRows}
          isMaxBox={!!maxLength && currentRightOptions?.length >= maxLength}
          resetScrollOnOptionsChange={resetScrollOnOptionsChange}
        />
      );
    }
    if (currentLeftOptions.length === 0) {
      return getCenterText(leftEmptyText);
    }
  };

  // 右侧内容
  const getRightNode = () => {
    // 如果提供了自定义渲染函数，直接使用
    if (rightCustomRender) {
      return rightCustomRender();
    }
    
    if (rightLoading) {
      return getCenterText(loadingNode);
    }
    if (currentRightOptions.length > 0) {
      const OptionComponent = dynamicHeight ? DynamicOptionBox : OptionBox;
      return (
        <OptionComponent
          ref={rightListRef}
          options={currentRightOptions}
          optionRender={rightOptionRender}
          virtual={virtual}
          dynamicHeight={dynamicHeight}
          rowHeight={rowHeight}
          listHeight={rightListHeight}
          maxVisibleRows={maxVisibleRows}
          resetScrollOnOptionsChange={resetScrollOnOptionsChange}
        />
      );
    }
    if (currentRightOptions.length === 0) {
      return getCenterText(rightEmptyText);
    }
  };

  return (
    <Modal
      size={modalSize || "l"}
      caption={titleNode}
      visible={visible}
      onClose={onCancel}
      className={classNames(className, 'dbh-transfer-modal-container')}
      style={style}
    >
      <Modal.Body>
        <Transfer
          className="dbh-transfer-container"
          leftCell={
            <Transfer.Cell
              className="dbh-transfer-container-left"
              title={leftTitle}
              scrollable={false}
              header={
                <>
                  {searchable && (
                    <SearchSelector
                      onSearch={onSearchEnter}
                      onSearchValueChange={onSearchChange}
                      options={currentSearchOptions}
                      placeholder={searchPlaceholder}
                      onClearSearch={onClearSearch}
                      className={className}
                    />
                  )}
                  {leftHeaderExtra}
                  {leftTabs && leftTabs.length > 0 && (
                    <TabsHeader
                      tabs={leftTabs}
                      activeId={leftActiveTabId || ''}
                      onChange={onLeftTabChange || (() => {})}
                    />
                  )}
                </>
              }
            >
              {getLeftNode()}
            </Transfer.Cell>
          }
          rightCell={
            <Transfer.Cell
              className="dbh-transfer-container-right"
              title={
                <div className="dbh-transfer-right-title">
                  <span className="dbh-transfer-right-select-info">
                    {dynamicString(
                      rightTitle,
                      {
                        length: currentRightOptions?.length,
                      },
                      '0',
                    )}
                    <span className="dbh-transfer-right-select-maxlength">
                      {maxLength &&
                        currentRightOptions?.length >= maxLength &&
                        dynamicString(maxLengthText, {
                          length: maxLength,
                        })}
                    </span>
                  </span>
                  {rightInfo && <span className = "dbh-transfer-right-info">{rightInfo}</span>}
                  {showClearAll && currentRightOptions?.length > 0 && (
                    <Button
                      type="link"
                      className="dbh-transfer-right-clear"
                      onClick={onClearAllOption}
                    >
                      {clearAllText}
                    </Button>
                  )}
                </div>
              }
              header={
                (rightSubTitle || quickOptions) && (
                  <div className="dbh-transfer-right-header">
                    <div className="dbh-transfer-right-header-title">
                      {rightSubTitle}
                    </div>

                    <div className="dbh-transfer-right-quick-options">
                      {quickOptions?.map((item) => {
                        const disabled =
                          rightLoading ||
                          (maxLength &&
                            currentRightOptions?.length >= maxLength &&
                            !currentQuickValue?.includes(item.value));

                        return (
                          <span
                            key={item.value}
                            className={classNames([
                              'dbh-transfer-right-quick-options-tag',
                              {
                                active: currentQuickValue?.includes(item.value),
                              },
                              {
                                disabled,
                              },
                            ])}
                            onClick={() => {
                              // disabled点击无反应
                              if (disabled) return;
                              onClickQuickOption(item.value);
                            }}
                          >
                            {getName(isEn, item.textEn, item.textCn)}
                          </span>
                        );
                      })}
                    </div>
                  </div>
                )
              }
            >
              {getRightNode()}
            </Transfer.Cell>
          }
        />
      </Modal.Body>
      <Modal.Footer>
        <Button
          type="primary"
          onClick={() => onConfirm?.(currentRightOptions)}
          disabled={saveEmptyDisable && currentRightOptions?.length < minLength}
        >
          {confirmText}
        </Button>
        <Button type="weak" onClick={onCancel}>
          {cancelText}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default forwardRef(TransferModal);
